/**
 * Background Jobs Widget
 * Виджет для отображения активных фоновых процессов в header
 */

import { useState, useEffect } from 'react';
import { useBackgroundJobsStore, BackgroundJob } from '../stores/backgroundJobsStore';
import { useAuthStore } from '../stores/authStore';
import {
  Clock,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
} from 'lucide-react';


interface BackgroundJobsWidgetProps {
  widgetRef?: React.RefObject<HTMLDivElement>;
  onJobClick?: (job: BackgroundJob) => void;
}

export default function BackgroundJobsWidget({ onJobClick, widgetRef }: BackgroundJobsWidgetProps) {
  const { 
    isConnected
  } = useBackgroundJobsStore();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  
  // Используем селектор для реактивности
  const activeJobs = useBackgroundJobsStore((state) => {
    const jobs = state.getActiveJobs();
    // Логирование для отладки
    if (jobs.length > 0) {
      console.log('[BackgroundJobsWidget] Active jobs:', jobs.map(j => ({
        jobId: j.jobId,
        status: j.status,
        progress: `${j.progress.current}/${j.progress.total} (${j.progress.percentage}%)`,
        currentStep: j.currentStep,
      })));
    }
    return jobs;
  });
  const [isExpanded, setIsExpanded] = useState(false);

  // Если не подключены И нет активных процессов, не показываем виджет
  if (!isConnected && activeJobs.length === 0) {
    return null;
  }

  const getJobIcon = (job: BackgroundJob) => {
    switch (job.status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'stopped':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-gray-500" />;
      default:
        return <Clock className="h-4 w-4 text-blue-500 animate-spin" />;
    }
  };

  const getJobStatusColor = (job: BackgroundJob) => {
    switch (job.status) {
      case 'completed':
        return 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-900';
      case 'failed':
        return 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-900';
      case 'stopped':
        return 'bg-yellow-50 dark:bg-yellow-950 border-yellow-300 dark:border-yellow-900';
      case 'cancelled':
        return 'bg-gray-50 dark:bg-zinc-900 border-gray-200 dark:border-zinc-800';
      default:
        return 'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-900';
    }
  };

  return (
    <div ref={widgetRef} className="relative">
      {/* Компактный виджет */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 px-3 py-2 bg-indigo-50 dark:bg-indigo-950 hover:bg-indigo-100 dark:hover:bg-indigo-900 rounded-lg border border-indigo-200 dark:border-indigo-900 transition-colors"
        title={`${activeJobs.length} активных процесса`}
      >
        <Clock className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
        <span className="text-sm font-medium text-indigo-900 dark:text-indigo-200">
          {activeJobs.length}
        </span>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
        )}
        {!isConnected && (
          <span className="h-2 w-2 bg-red-500 rounded-full" title="Не подключено к серверу" />
        )}
      </button>

      {/* Раскрывающийся список процессов */}
      {isExpanded && (
        <div className="absolute top-full right-0 mt-2 w-80 bg-white dark:bg-zinc-900 rounded-lg shadow-lg border border-gray-200 dark:border-zinc-800 z-50 max-h-96 overflow-y-auto">
          <div className="p-3 border-b border-gray-200 dark:border-zinc-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
              Активные процессы ({activeJobs.length})
            </h3>
          </div>

          <div className="divide-y divide-gray-100 dark:divide-zinc-800">
            {activeJobs.map((job) => (
              <div
                key={job.jobId}
                onClick={() => {
                  if (onJobClick) {
                    onJobClick(job);
                  }
                  setIsExpanded(false);
                }}
                className={`p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-800/60 transition-colors ${getJobStatusColor(job)}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {getJobIcon(job)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-zinc-100 truncate">
                          {job.name}
                        </p>
                        {job.status === 'stopped' && (
                          <span className="text-xs text-yellow-700 dark:text-yellow-300 font-medium whitespace-nowrap">
                            ⚠️ Остановлена
                          </span>
                        )}
                        {job.status === 'cancelled' && (
                          <span className="text-xs text-gray-700 dark:text-zinc-200 font-medium whitespace-nowrap">
                            🚫 Отменена
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 dark:text-zinc-400 whitespace-nowrap">
                        {job.progress.percentage}%
                      </span>
                    </div>
                    
                    <p className="text-xs text-gray-600 dark:text-zinc-300 mb-2 truncate">
                      {job.status === 'stopped' 
                        ? 'Задача остановлена из-за непредвиденной ошибки (приложение было перезапущено)' 
                        : job.status === 'cancelled'
                        ? 'Задача отменена пользователем'
                        : job.currentStep}
                    </p>
                    
                    {/* Информация о пользователе (только для админов) */}
                    {isAdmin && job.userName && (
                      <div className="flex items-center gap-1 mb-2">
                        <span className="text-xs text-gray-500 dark:text-zinc-400">
                          👤 {job.userName}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-zinc-500">
                          (ID: {job.userId})
                        </span>
                      </div>
                    )}
                    
                    {/* Кнопка завершения для зависших задач */}
                    {job.status === 'stopped' && (
                      <div className="mt-2 mb-2">
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const { jobsAPI } = await import('../lib/api');
                              await jobsAPI.finishStoppedJob(job.jobId);
                              // Задача будет обновлена через WebSocket
                            } catch (error) {
                              console.error('Failed to finish stopped job:', error);
                            }
                          }}
                          className="w-full text-xs px-2 py-1 bg-yellow-100 dark:bg-yellow-950 hover:bg-yellow-200 dark:hover:bg-yellow-900 text-yellow-800 dark:text-yellow-300 rounded border border-yellow-300 dark:border-yellow-900 transition-colors"
                        >
                          Завершить задачу
                        </button>
                      </div>
                    )}
                    
                    {/* Прогресс-бар */}
                    <div className="w-full bg-gray-200 dark:bg-zinc-700 rounded-full h-1.5 overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          job.status === 'completed'
                            ? 'bg-green-500'
                            : job.status === 'failed'
                            ? 'bg-red-500'
                            : job.status === 'stopped'
                            ? 'bg-yellow-500'
                            : job.status === 'cancelled'
                            ? 'bg-gray-500'
                            : 'bg-blue-500'
                        }`}
                        style={{ width: `${Math.min(100, job.progress.percentage)}%` }}
                      />
                    </div>
                    
                    {/* Подсказка: клик для деталей */}
                    {onJobClick && (
                      <p className="mt-1.5 text-[10px] text-gray-400 dark:text-zinc-500">
                        Нажмите для подробностей
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {activeJobs.length === 0 && (
            <div className="p-4 text-center text-sm text-gray-500 dark:text-zinc-400">
              Нет активных процессов
            </div>
          )}
        </div>
      )}
    </div>
  );
}

